import type { FC } from "react";
import { Breakpoint, useMediaQuery } from "@stubhub/broadway-providers";
import { Heading, Text } from "@stubhub/broadway-web";
import { ChevronRightIcon } from "@stubhub/broadway-web/icons/functional";
import type { CwyloCarousel, CwyloTile } from "src/http/types";
import type { CarouselSize } from "src/types/shared/common";
import {
  CarouselId,
  CarouselItemType,
} from "src/website-apps/shared/components/carousel-module/carousel-analytics-event";
import { HOME_CAROUSEL_IDS } from "src/website-apps/shared/components/responsive-carousel/constants";
import { ResponsiveCarousel } from "src/website-apps/shared/components/responsive-carousel/ResponsiveCarousel";
import { useCarouselDimensions } from "src/website-apps/shared/hooks/useCarouselDimensions";
import { cwyloCarouselRecipe } from "./cwylo-carousel.styles";

interface Props {
  readonly carousel: CwyloCarousel;
  readonly carouselSize: CarouselSize | undefined;
}

/**
 * Home "Continue where you left off" carousel (DMS-683, exp 104507). The Collections Service
 * supplies the event header, the date badge, every string, and the zone or listing tiles; this
 * component renders them and links each tile to the URL the backend resolved.
 */
export const CwyloCarouselOnHome: FC<Props> = ({ carousel, carouselSize }) => {
  const styles = cwyloCarouselRecipe();
  const isMobile = !useMediaQuery(Breakpoint.Tablet);
  const carouselDim = useCarouselDimensions(carouselSize);
  const items = carousel.items ?? [];
  const badges = carousel.dateBadge;

  if (!carousel.title || items.length === 0) {
    return null;
  }

  const renderItem = (item: CwyloTile, index: number) => (
    <a
      key={`${item.listingId ?? "tile"}-${index}`}
      className={styles.card}
      href={item.url}
      aria-label={item.accessibilityLabel ?? item.title}
    >
      {item.imageUrl ? <img className={styles.image} src={item.imageUrl} alt="" loading="lazy" /> : null}
      <div className={styles.cardBody}>
        <Text variant="label" size="small" className={styles.cardTitle}>
          {item.title}
        </Text>
        {item.subtitle ? (
          <Text variant="body" size="small" color="text.subtle">
            {item.subtitle}
          </Text>
        ) : null}
      </div>
    </a>
  );

  return (
    <section className={styles.root} aria-label={carousel.accessibilityLabel ?? carousel.title}>
      <div className={styles.header}>
        {badges ? (
          <div className={styles.dateBadge} aria-hidden>
            {badges.day ? (
              <Text variant="label" size="extra-small" className={styles.dateBadgeDay}>
                {badges.day}
              </Text>
            ) : null}
            <Text variant="body" size="medium" numeric className={styles.dateBadgeDayOfMonth}>
              {badges.dayOfMonth}
            </Text>
            <Text variant="label" size="extra-small" className={styles.dateBadgeMonth}>
              {badges.month}
            </Text>
            {badges.year ? (
              <Text variant="label" size="extra-small" className={styles.dateBadgeYear}>
                {badges.year}
              </Text>
            ) : null}
          </div>
        ) : null}
        <div className={styles.headerText}>
          <Heading variant="title" size="small" as="h2" className={styles.title}>
            {carousel.title}
          </Heading>
          {carousel.subtitle ? (
            <Text variant="body" size="small" color="text.subtle">
              {carousel.subtitle}
            </Text>
          ) : null}
          {carousel.note ? (
            <Text variant="label" size="small" color="text.subtle">
              {carousel.note}
            </Text>
          ) : null}
        </div>
        {carousel.seeAllUrl ? (
          <a
            className={styles.seeAll}
            href={carousel.seeAllUrl}
            aria-label={carousel.seeAllAccessibilityLabel ?? carousel.title}
          >
            <ChevronRightIcon size={20} />
          </a>
        ) : null}
      </div>
      <ResponsiveCarousel<CwyloTile>
        carouselId={HOME_CAROUSEL_IDS.continueWhereYouLeftOff}
        analyticsCarouselId={CarouselId.ContinueWhereYouLeftOff}
        analyticsItemType={CarouselItemType.Event}
        size={carouselDim.carouselSize}
        gapPx={carouselDim.gapPx}
        imageHeightHint={carouselDim.imageSize.height}
        hidePaginationButtons={isMobile}
        hasHoverEffect={!isMobile}
        items={items}
        renderItem={renderItem}
      />
    </section>
  );
};
